import { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useServerErrors } from '../../Shared/api';
import { Form, User } from '../../Shared/types';
import { Button } from '../../Shared/UI/Button';
import { Card } from '../../Shared/UI/Card';
import { Checkbox } from '../../Shared/UI/Checkbox';
import CheckboxLabel from '../../Shared/UI/CheckboxLabel';
import { FadeIn } from '../../Shared/UI/FadeIn';
import InputGroup from '../../Shared/UI/InputGroup';
import InputLabel from '../../Shared/UI/InputLabel';
import ServerErrors from '../../Shared/UI/ServerErrors';
import { Charity } from '../types';
import Layout from '../UI/Layout';

export interface EditEventSettingsFormProps extends Form {
  charity: Charity & {
    users: User[];
    new_event_notification_users: User[];
  };
}

export default function EditEventSettingsForm({
  charity,
  onSubmit,
  serverErrors,
  loading,
}: EditEventSettingsFormProps) {
  const { t } = useTranslation();

  const form = useForm({
    defaultValues: {
      new_event_notification_enabled: charity.new_event_notification_enabled,
      new_event_notification_users: charity.new_event_notification_users.map((user) => user.id),
    },
  });

  const {
    register, handleSubmit, watch, setValue,
    formState: { defaultValues, errors },
  } = form;

  const newEventNotificationEnabled = watch('new_event_notification_enabled');
  const newEventNotificationUsers = watch('new_event_notification_users');

  useServerErrors(form, serverErrors);

  const submit = async (data: typeof defaultValues) => {
    await onSubmit({
      ...data,
    });
  };

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Card panels header footer>
        <div>
          <h3>
            {t('backend:edit_event_settings')}
          </h3>
        </div>
        <div className="space-y-6">
          {serverErrors && <ServerErrors errors={serverErrors} scrollIntoView />}

          <div>
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:notifications')}
            </h4>
            <div className="text-slate-500">
              {t('backend:new_event_notification_description')}
            </div>
          </div>

          <InputGroup>
            <InputLabel valid={!errors.new_event_notification_users}>
              {t('backend:new_event_notification')}
            </InputLabel>
            <CheckboxLabel htmlFor="new_event_notification_enabled">
              <Checkbox
                {...register('new_event_notification_enabled')}
                id="new_event_notification_enabled"
                className="mt-1"
              />
              <span>
                {t('backend:enable_notifications')}
              </span>
            </CheckboxLabel>
          </InputGroup>
          <FadeIn
            when={newEventNotificationEnabled}
            render={() => (
              <InputGroup>
                <InputLabel valid={!errors.new_event_notification_users}>
                  {t('backend:send_notifications_to')}
                </InputLabel>
                <CheckboxLabel htmlFor="all_users">
                  <Checkbox
                    onChange={() => setValue('new_event_notification_users', [])}
                    disabled={newEventNotificationUsers.length === 0}
                    checked={newEventNotificationUsers.length === 0}
                    id="all_users"
                    className="mt-1"
                  />
                  <span>
                    {t('backend:all_users')}
                  </span>
                </CheckboxLabel>
                {charity.users.map((user) => (
                  <CheckboxLabel htmlFor={`user_${user.id}`} key={user.id}>
                    <Checkbox
                      {...register('new_event_notification_users')}
                      value={user.id}
                      id={`user_${user.id}`}
                      className="mt-1"
                    />
                    <span>
                      {user.full_name}
                    </span>
                  </CheckboxLabel>
                ))}
              </InputGroup>
            )}
          />
        </div>
        <div className="text-right">
          <Button
            type="submit"
            loading={loading}
            className="ml-auto"
          >
            {t('backend:save')}
          </Button>
        </div>
      </Card>
    </form>
  );
}

EditEventSettingsForm.layout = (page: ReactNode) => <Layout>{page}</Layout>;
